function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
} 

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}
/* 
document.addEventListener('DOMContentLoaded', () => {
  // Function to hide unavailable options
  function hideUnavailableOptions() {
    document.querySelectorAll('select').forEach(select => {
      select.querySelectorAll('option').forEach(option => {
        if (option.textContent.toLowerCase().includes('unavailable')) {
          option.remove(); // completely remove instead of hide
        }
      });

      // If the currently selected option includes 'Unavailable', reset it
      if (select.options.length && select.selectedOptions[0]?.textContent.toLowerCase().includes('unavailable')) {
        select.selectedIndex = 0; // reset to first available
      }
    });
  }

  // Run once on page load
  hideUnavailableOptions();

  // Watch for changes in the DOM (Shopify re-renders selects dynamically)
  const observer = new MutationObserver(() => {
    hideUnavailableOptions();
  });

  observer.observe(document.body, { childList: true, subtree: true });

  // Also run on manual change
  document.addEventListener('change', hideUnavailableOptions);
});

*/
document.addEventListener('DOMContentLoaded', () => {
  const PLACEHOLDER_TEXT = 'Choose an Option';
  const ENABLE_DEBUG_LOGS = false;

  // global flag to remember user made a choice (even after select re-renders)
  let userHasSelected = false;

  function log(...args) {
    if (ENABLE_DEBUG_LOGS) console.log('[VariantFix]', ...args);
  }

  function isProductOptionSelect(el) {
    if (!(el instanceof HTMLSelectElement)) return false;
    if (el.name && el.name.indexOf('options[') === 0) return true;
    if (el.closest && el.closest('form[action*="/cart"]')) return true;
    if (el.closest && el.closest('[data-product-form], .product-form, form.product-form')) return true;
    return false;
  }

  function ensurePlaceholder(select) {
    const first = select.querySelector('option:first-child');
    if (first && first.dataset && first.dataset.ourPlaceholder === 'true') return;

    const placeholder = document.createElement('option');
    placeholder.textContent = PLACEHOLDER_TEXT;
    placeholder.value = '';
    placeholder.disabled = true;
    placeholder.selected = true;
    placeholder.dataset.ourPlaceholder = 'true';
    select.insertBefore(placeholder, select.firstChild);
    log('Inserted placeholder for select', select);
  }

  function cleanUnavailableAndNormalize(select) {
    if (!isProductOptionSelect(select)) return;

    // 🧠 Skip resetting if user already interacted
    if (userHasSelected) {
      log('Skipping cleanup after user selection');
      return;
    }

    const options = Array.from(select.options);
    options.forEach(opt => {
      const txt = (opt.textContent || '').toLowerCase();
      if (txt.includes('unavailable') || (txt.trim() === '' && !opt.dataset.ourPlaceholder)) {
        opt.remove();
      }
    });

    const remainingOptions = Array.from(select.options).filter(
      o => !(o.dataset && o.dataset.ourPlaceholder === 'true')
    );

    // Skip placeholder logic for single-option selects
// Always add placeholder unless no real options exist
if (remainingOptions.length >= 1) {
    ensurePlaceholder(select);
    select.selectedIndex = 0; // force placeholder visible
}

    ensurePlaceholder(select);

    // If nothing selected, make placeholder active
    const sel = select.selectedOptions[0];
    if (!sel || (sel.dataset && sel.dataset.ourPlaceholder === 'true')) {
      select.selectedIndex = 0;
    }
  }

  function processAllProductSelects() {
    const selects = Array.from(document.querySelectorAll('select')).filter(isProductOptionSelect);
    selects.forEach(cleanUnavailableAndNormalize);
  }

  // === Observe dynamic reloads ===
  let mutationTimeout = null;
  const observer = new MutationObserver((mutationsList) => {
    if (mutationTimeout) clearTimeout(mutationTimeout);
    mutationTimeout = setTimeout(() => {
      const addedNodes = mutationsList.flatMap(m => Array.from(m.addedNodes));
      const hasNewSelect = addedNodes.some(
        node => node.nodeType === 1 && node.querySelector && node.querySelector('select')
      );
      if (hasNewSelect) processAllProductSelects();
      mutationTimeout = null;
    }, 200);
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Shopify section reloads
  ['shopify:section:load', 'shopify:section:reorder', 'shopify:section:select'].forEach(evt =>
    window.addEventListener(evt, processAllProductSelects)
  );

  // Initial runs
  setTimeout(processAllProductSelects, 400);
  setTimeout(processAllProductSelects, 1200);

  // ✅ When user makes a selection, remember it globally
  document.addEventListener(
    'change',
    (e) => {
      const sel = e.target;
      if (sel.tagName === 'SELECT' && isProductOptionSelect(sel)) {
        userHasSelected = true;
        log('User made a selection, disabling resets.');
      }
    },
    true
  );
});




  const containers = document.querySelectorAll('.media-type-image');

containers.forEach(container => {
  const image = container.querySelector('.image-magnify-lightbox');

  container.addEventListener('mousemove', function (e) {
    const rect = container.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    image.style.transformOrigin = `${x}% ${y}%`;
    image.style.transform = 'scale(2)';
  });

  container.addEventListener('mouseleave', function () {
    image.style.transformOrigin = 'center center';
    image.style.transform = 'scale(1)';
  });
});


(function () {

  const PLACEHOLDER_TEXT = 'Choose an Option';
  let lastChangedSelectName = null;

  function isProductOptionSelect(el) {
    return el instanceof HTMLSelectElement &&
      (el.name?.startsWith('options[') ||
       el.closest('form[action*="/cart"], .product-form, [data-product-form]'));
  }

  function ensurePlaceholder(select) {
    const first = select.options[0];
    if (first && first.value === '') return;

    const opt = document.createElement('option');
    opt.value = '';
    opt.textContent = PLACEHOLDER_TEXT;
    opt.disabled = true;
    opt.selected = true;
    select.insertBefore(opt, select.firstChild);
  }

  // 1️⃣ Track which dropdown user changed
  document.addEventListener('change', (e) => {
    if (isProductOptionSelect(e.target)) {
      lastChangedSelectName = e.target.name;
    }
  }, true);

  // 2️⃣ Observe Shopify DOM rebuilds
  const observer = new MutationObserver(() => {
    if (!lastChangedSelectName) return;

    // 🔥 LET SHOPIFY FINISH VARIANT SYNC FIRST
    setTimeout(() => {

      const selects = Array.from(document.querySelectorAll('select'))
        .filter(isProductOptionSelect);

      const changedIndex = selects.findIndex(
        s => s.name === lastChangedSelectName
      );

      if (changedIndex === -1) return;

      selects.forEach((select, index) => {
        if (index <= changedIndex) return;

        ensurePlaceholder(select);
        select.selectedIndex = 0; // visual reset only
      });

    }, 1); // 🔥 critical delay

  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

})();


document.addEventListener('DOMContentLoaded', () => {

  function parsePrice(text) {
    if (!text) return null;
    const num = parseFloat(text.replace(/[^0-9.]/g, ''));
    return isNaN(num) ? null : num;
  }

  function toggleZeroPrice() {
    document.querySelectorAll('.price-item--regular').forEach(priceEl => {

      const price = parsePrice(priceEl.textContent);
 //console.log('vvv'); 
      // ⛔ Ignore if price not ready yet
      if (price === null) return;

      const wrapper =
        priceEl.closest('.price') ||
        priceEl.closest('.product__info') ||
        priceEl;

      // ✅ Hide ONLY if exactly zero
      if (price === 0) {
       
        wrapper.style.display = 'none';
      } else {
        // ✅ Restore if price > 0
        wrapper.style.display = '';
      }
    });
  }

  // Initial (slightly delayed to let Shopify finish)
  setTimeout(toggleZeroPrice, 300);

  // Observe Shopify variant & price updates
  const observer = new MutationObserver(() => {
    toggleZeroPrice();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true
  });

});
 
//snj0
//if (window.location.pathname.includes('heavy-duty-courier-bag-with-arcolock-7')) { 
if (window.location.pathname.includes('rifkin-safety-sac')||
  window.location.pathname.includes('heavy-duty-courier-sacs')||
  window.location.pathname.includes('keyless-security-seals')||
  window.location.pathname.includes('collapsible-supply-bag-keyless-security-c2-89a-c2-a2')||
  window.location.pathname.includes('rolling-supply-bag-with-keyless-security-c2-89a-c2-a2-and-heavy-duty-wheels')||
  window.location.pathname.includes('fire-shield-courier-sacs-locking')||
  window.location.pathname.includes('briefcase-style-locking-courier-bag')||
  window.location.pathname.includes('light-duty-courier-keyless-securityaa-c2-89a-c2-a2')||
  window.location.pathname.includes('fire-shield-briefcase-style-courier-bag-arcolock-7-copy')||
  window.location.pathname.includes('post-office-type-mail-bags')||
  window.location.pathname.includes('long-zipper-bags')||
  window.location.pathname.includes('trans-saciiaiaa-expandable-trans-saciiaiaa')||
  window.location.pathname.includes('atm-courier-bag-with-keyless-security-c2-89a-c2-a2')||
  window.location.pathname.includes('poll-book-value-kit-padded-zipper-bag-3-laptop-sleeves')||
  window.location.pathname.includes('poll-book-value-kit-padded-keyless-security')||
  window.location.pathname.includes('4-wheel-rolling-supply-bag-with-keyless-security')||
  window.location.pathname.includes('dual-lock-4-wheel-rolling-ballot-bag-with-keyless-security')||
  window.location.pathname.includes('padlock-seals-with-scored-wire-hasp')||
  window.location.pathname.includes('6-compartment-coin-bags')||
  window.location.pathname.includes('plastic-coin-trays')||
  window.location.pathname.includes('chipboard-coin-boxes')||
  window.location.pathname.includes('cartridge-coin-wrappers')||
  window.location.pathname.includes('continuous-printed-rolls')||
  window.location.pathname.includes('white-kraft-currency-bands')||
  window.location.pathname.includes('quik-stik-currency-bands')||
  window.location.pathname.includes('provisional-ballot-bags-with-keyless-security')||
  window.location.pathname.includes('secur-a-ties-8-shipping-tags')||
  window.location.pathname.includes('2-wheel-rolling-supply-bag-keyless-securityaa-c2-89a-c2-a2')||
  window.location.pathname.includes('3-ring-binder-pencil-bag')||
  window.location.pathname.includes('3-ring-binder-supply-organizer-with-keyless-security')||
  window.location.pathname.includes('4-wheel-ballot-catcher-bags-shipped-in-3-days-from-receipt-of-order')||
  window.location.pathname.includes('4-wheel-collapsible-supply-bag-with-height-extenders')||
  window.location.pathname.includes('4-wheel-technician-rolling-supply-bag-with-keyless-security')||
  window.location.pathname.includes('5-compartment-coin-cash-bags')||
  window.location.pathname.includes('sign-stands')||
  window.location.pathname.includes('ar-15-style-locking-security-firearm-bag')||
  window.location.pathname.includes('anchor-seals-numbered')||
  window.location.pathname.includes('arcolite-2100')||
  window.location.pathname.includes('ballot-collection-bag-for-use-with-ds200')||
  window.location.pathname.includes('ballot-transfer-bag-zipper')||
  window.location.pathname.includes('ballot-transfer-bag-with-keyless-security-c2-89a-c2-a2')||
  window.location.pathname.includes('caddy-cone-sign-carrier')||
  window.location.pathname.includes('cart-covers')||
  window.location.pathname.includes('classroom-cell-phone-bags-with-keyless-security')||
  window.location.pathname.includes('classroom-cell-phone-kit-with-arcolock-7')||
  window.location.pathname.includes('clear-ppe-transport-bag')||
  window.location.pathname.includes('clear-top-loading-badge-holder')||
  window.location.pathname.includes('collapsible-ballot-bag-with-keyless-security-slot')||
  window.location.pathname.includes('collapsible-cart')||
  window.location.pathname.includes('collapsible-locking-organizer')||
  window.location.pathname.includes('come-back-bag')||
  window.location.pathname.includes('data-sac-with-arcolock-7')||
  window.location.pathname.includes('deluxe-stand-display-kit-double-sided-literature')||
  window.location.pathname.includes('deluxe-stand-literature-display-kit-one-banner')||
  window.location.pathname.includes('direct-sort-trans-sac')||
  window.location.pathname.includes('direct-sort-locking-mail-pouch')||
  window.location.pathname.includes('drawstring-coin-bags')||
  window.location.pathname.includes('drive-up-bank-teller-bags')||
  window.location.pathname.includes('drop-box-ballot-catching-bags')||
  window.location.pathname.includes('dust-cover-for-dominion-precinct')||
  window.location.pathname.includes('cart-covers-for-diebold-accu-vote-tsx-c2-89a-c2-a2-machines')||
  window.location.pathname.includes('evid-carrier-with-keyless-securityaa-c2-89a-c2-a2')||
  window.location.pathname.includes('election-official-name-tag-inserts')||
  window.location.pathname.includes('equipment-pinch-seal')||
  window.location.pathname.includes('executive-attache')||
  window.location.pathname.includes('keyless-securityaa-c2-89a-c2-a2-expandable-sac')||
  window.location.pathname.includes('fast-seals')||
  window.location.pathname.includes('firearm-security-bag')||
  window.location.pathname.includes('portable-locking-safe')||
  window.location.pathname.includes('flag-case')||
  window.location.pathname.includes('hanging-folders')||
  window.location.pathname.includes('hanging-ballot-bags-with-arcolock-7iii_iaciiaiia')||
  window.location.pathname.includes('heavy-duty-courier-bag-non-lock')||
  window.location.pathname.includes('incognito-hidden-lock-backpack')||
  window.location.pathname.includes('incognito-hidden-lock-executive-security-bag')||
  window.location.pathname.includes('hidden-lock-messenger-bag')||
  window.location.pathname.includes('keyless-securityiiaii_iii_iaiiaia-c2-a2-bag-jbc-transport-case')||
  window.location.pathname.includes('judgesbag')||
  window.location.pathname.includes('key-hole-signals-safe-deposit-boxes')||
  window.location.pathname.includes('key-transport-bag')||
  window.location.pathname.includes('key-transport-bag-with-keyless-security')||
  window.location.pathname.includes('keyless-securityiiaii_iii_iaiiaia-c2-a2-transport-fits-hart-e-scan')||
  window.location.pathname.includes('keyless-security-c2-89a-c2-a2-bag-for-peb-modules')||
  window.location.pathname.includes('keyless-securityaa-c2-89a-c2-a2-fireshield-courier-bag')||
  window.location.pathname.includes('keyless-securitya-mini-padlock')||
  window.location.pathname.includes('keyless-security-c2-89a-c2-a2-memory-card-transport-bag')||
  window.location.pathname.includes('keyless-securityaa-c2-89a-c2-a2-pouch')||
  window.location.pathname.includes('keyless-security-c2-89a-c2-a2-rolling-transport-with-retractable-handle')||
  window.location.pathname.includes('keyless-security-seals-for-arcolite-2100')||
  window.location.pathname.includes('laptop-sleeves')||
  window.location.pathname.includes('light-duty-courier-non-locking')||
  window.location.pathname.includes('light-duty-courier-bag-with-arcolock-7iiaiaa')||
  window.location.pathname.includes('locking-jewelry-and-valuables-bag')||
  window.location.pathname.includes('locking-medication-bags')||
  window.location.pathname.includes('mini-briefcase-style-courier-pouch')||
  window.location.pathname.includes('specimen-bags')||
  window.location.pathname.includes('keyless-security-c2-89a-c2-a2-med-transport-bag')||
  window.location.pathname.includes('multi-pocket-data-key-bag')||
  window.location.pathname.includes('mega-twister-security-seals')||
  window.location.pathname.includes('open-top-pre-printed-tote')||
  window.location.pathname.includes('open-top-totes')||
  window.location.pathname.includes('padded-cash-recycler-canister-bag')||
  window.location.pathname.includes('electronic-poll-book-kit-keyless-securityiiaii_iii_iaiiaia-c2-a2-bag')||
  window.location.pathname.includes('padded-printer-bag')||
  window.location.pathname.includes('ereadertablet-carriers')||
  window.location.pathname.includes('pocket-folios')||
  window.location.pathname.includes('pinch-seals')||
  window.location.pathname.includes('zipper-bag-for-postersign-transport')||
  window.location.pathname.includes('proof-bag')||
  window.location.pathname.includes('provisional-ballot-bags')||
  window.location.pathname.includes('arco-id-tamper-evident-labels')||
  window.location.pathname.includes('recyclable-i-voted-stylus')||
  window.location.pathname.includes('safety-vests')||
  window.location.pathname.includes('the-rifkin-safety')||
  window.location.pathname.includes('provisional-ballot-bags-shipped-in-3-days-from-receipt-of-order')||
  window.location.pathname.includes('rf-shielding-bag-keyless-security')||
  window.location.pathname.includes('rifkin-safety-sac-lock-bag-for-notary-publics')||
  window.location.pathname.includes('keyless-securityiiaii_iii_iaiiaia-c2-a2-rolling-transport-fits-hart-jbc')||
  window.location.pathname.includes('rolling-padded-transport-bag-with-keyless-security')||
  window.location.pathname.includes('safe-deposit-box-key-envelopes')||
  window.location.pathname.includes('secur-a-file-c2-89a-c2-a2')||
  window.location.pathname.includes('mailsafeaa-c2-89a-c2-a2-mail-sac')||
  window.location.pathname.includes('sign-bags')||
  window.location.pathname.includes('single-canister-transport-bag-designed-for-ess-expressvote-xl')||
  window.location.pathname.includes('small-change-purse')||
  window.location.pathname.includes('snap-top-tote-bag-navy-or-red-skirt')||
  window.location.pathname.includes('totes-snap-top-wstandup-handles')||
  window.location.pathname.includes('stackable-supply-bag-with-keyless-security')||
  window.location.pathname.includes('selfie-stand-kit')||
  window.location.pathname.includes('selfie-stand-kit-2')||
  window.location.pathname.includes('standard-shipping-bags')||
  window.location.pathname.includes('sneeze-guard-transport-bag')||
  window.location.pathname.includes('stand-up-ballot-display-kit')||
  window.location.pathname.includes('stand-up-provisional-ballot-bag-with-keyless-security-slot')||
  window.location.pathname.includes('tsx-c2-89a-c2-a2-carry-bag-with-keyless-security')||
  window.location.pathname.includes('tsx-c2-89a-c2-a2-tablet-bag-with-keyless-security-c2-89a-c2-a2')||
  window.location.pathname.includes('technician-carry-bag-with-keyless-security')||
  window.location.pathname.includes('top-side-zipper-bags')||
  window.location.pathname.includes('key-rings-tamper-proof')||
  window.location.pathname.includes('usa-lanyard')||
  window.location.pathname.includes('vinyl-side-courier-with-arcolock-7iiaiaa')||
  window.location.pathname.includes('wheelchair-pouch')||
  window.location.pathname.includes('priority-mail-trans-saciiaiaa')||
  window.location.pathname.includes('secur-a-ties-8-shipping-tags-2')||
  window.location.pathname.includes('zipper-top-tote-bag')||
  window.location.pathname.includes('zipper-transport-bags')||
  window.location.pathname.includes('electronic-poll-book-kit-zipper-bag')||
  window.location.pathname.includes('work-flow-bag')||
  window.location.pathname.includes('pull-seals')||
  window.location.pathname.includes('briefcase-style-courier-bag-with-arcolock-7')||
  window.location.pathname.includes('collapsible-supply-bag-with-arcolock-7')||
  window.location.pathname.includes('direct-sort-mail-courier-bag-with-arcolock-7')||
  window.location.pathname.includes('executive-attache-gsa-1')||
  window.location.pathname.includes('expandable-trans-sac')||
  window.location.pathname.includes('fire-shield')||
  window.location.pathname.includes('firearm-security-bag-gsa-1')||
  window.location.pathname.includes('incognito-hidden-lock-backpack-gsa-1')||
  window.location.pathname.includes('keyless-security')||
  window.location.pathname.includes('light-duty-courier-bag-with-arcolock-7')||
  window.location.pathname.includes('heavy-duty-courier-bag-with-arcolock-7')||
  window.location.pathname.includes('mailsafe%E2%84%A2-mail-sac-with-keyless-security%E2%84%A2-gsa')||
  window.location.pathname.includes('post-office-style-mail-bags-gsa-1')||
  window.location.pathname.includes('vinyl-side-courier-with-arcolock-7')||
  window.location.pathname.includes('trans-sac')||
  window.location.pathname.includes('data-module-carrier-15-pocket')||
  window.location.pathname.includes('standup-provisional-ballot-bag-with-keyless-securityaa-c2-89a-c2-a2-slot')||
  window.location.pathname.includes('top-zipper-coin-bags')||
  window.location.pathname.includes('dual-lock-stand-up-provisional-ballot-bag-with-keyless-security')||
  window.location.pathname.includes('dominion-imagecast-ballot-catching-system')||
  window.location.pathname.includes('voter-authority-slip-bags')||
  window.location.pathname.includes('emergency-precinct-set-up-kits')||
  window.location.pathname.includes('long-gun-locking-security-bag')) {
   console.log("specific Prod:");
document.addEventListener("DOMContentLoaded", function () {

    const qtySelector = 'input[name="quantity"]';
    const priceSelector = '.price'; // 🔥 change if needed
    const tableSelector = '#lb-vd-table tbody tr';

    function getQty() {
        const qtyInput = document.querySelector(qtySelector);
        return qtyInput ? parseInt(qtyInput.value) || 1 : 1;
    }

    function getTierPrice(qty) {
        const rows = document.querySelectorAll(tableSelector);

        for (let row of rows) {
            let rangeText = row.querySelector('[data-label="Buy quantity"]').innerText.trim();
            let priceText = row.querySelector('.money').innerText.trim();
            let price = parseFloat(priceText.replace(/[^0-9.]/g, ''));

            if (rangeText.includes('+')) {
                let min = parseInt(rangeText.match(/\d+/)[0]);
                if (qty >= min) return price;
            }

            let match = rangeText.match(/(\d+)\s*-\s*(\d+)/);
            if (match) {
                let min = parseInt(match[1]);
                let max = parseInt(match[2]);
                if (qty >= min && qty <= max) return price;
            }
        }

        return null;
    }

    // 🔥 Get paid custom option price
    function getCustomOptionPrice() {

    let extra = 0;

    document.querySelectorAll('select.pplraddprice').forEach(select => {

        // ✅ SKIP hidden / duplicate selects
        if (select.offsetParent === null) return;

        let selectedOption = select.options[select.selectedIndex];
        if (!selectedOption) return;

        let price = parseFloat(selectedOption.dataset.pplr_price || 0);

        if (price > 0) {

            extra += price;

            console.log("Custom Option Selected:");
            console.log("Label:", selectedOption.text.trim());
            console.log("Price from data-pplr_price:", price);
            console.log("-------------------");
        }
    });

    // 🔹 Handle HIDDEN INPUT (Font etc.)
    document.querySelectorAll('input.pplraddprice[type="hidden"]').forEach(input => {

        let price = parseFloat(input.dataset.pplr_price || 0);
        let value = input.value || '';

        // Only if value exists AND price exists
        if (value.trim() !== '' && price > 0) {

            extra += price;

            console.log("🔵 Hidden Option:");
            console.log("Label:", input.getAttribute('data_name'));
            console.log("Selected:", value);
            console.log("Price:", price);
            console.log("-------------------");
        }
    });    
    console.log("Total Custom Extra (once):", extra);
    //console.log("================================");

    return extra;
}   

    function updatePrice() {

      let qty = getQty();
      let tierPrice = getTierPrice(qty);
      let customExtra = getCustomOptionPrice();
      let priceEl = document.querySelector(priceSelector);

      if (!priceEl) return;

      if (tierPrice !== null) {

          // 🔥 Correct Formula
          //let total = (tierPrice * qty) + customExtra;
          let total = (tierPrice + customExtra) * qty;
          priceEl.innerText = '$' + total.toFixed(2);

          //console.log("Qty:", qty);
          //console.log("Tier Unit:", tierPrice);
         // console.log("Tier Total:", tierPrice * qty);
          //console.log("Custom (once):", customExtra);
          //console.log("Final Total:", total);
          //console.log("-----------");
      }
    }

    // 🔥 Listen to everything
    document.addEventListener("input", function () { 
        setTimeout(updatePrice, 300);
    });

    document.addEventListener("change", function () {
        setTimeout(updatePrice, 300);
    });

    document.addEventListener("click", function () {
        setTimeout(updatePrice, 300);
    });

});
}
else {

document.addEventListener("DOMContentLoaded", function () {

    console.log("Without Tier:");

    const qtySelector = 'input[name="quantity"]';
    const priceSelector = '.price';

    const priceEl = document.querySelector(priceSelector);

    if (!priceEl) return;

    const originalPrice = parseFloat(
        priceEl.innerText.replace(/[^0-9.]/g, '')
    ) || 0;

    console.log("Original Unit Price:", originalPrice);

    function getQtyWithout() {
        const qtyInput = document.querySelector(qtySelector);
        return qtyInput ? parseInt(qtyInput.value) || 1 : 1;
    }

    function getCustomOptionPriceWithout() {

        let extra = 0;

        document.querySelectorAll('select.pplraddprice').forEach(select => {

            if (select.offsetParent === null) return;

            let selectedOption = select.options[select.selectedIndex];
            if (!selectedOption) return;

            let price = parseFloat(selectedOption.dataset.pplr_price || 0);

            if (price > 0) {
                extra += price;
            }
        });

        document.querySelectorAll('input.pplraddprice[type="hidden"]').forEach(input => {

            let price = parseFloat(input.dataset.pplr_price || 0);
            let value = input.value || '';

            if (value.trim() !== '' && price > 0) {
                extra += price;
            }
        });

        return extra;
    }

    function updatePriceWithout() {

        let qty = getQtyWithout();
        let customExtra = getCustomOptionPriceWithout();

       let total = (originalPrice + customExtra) * qty;

       // priceEl.innerText = '$' + total.toFixed(2);
       if (window.location.pathname !== '/') {
          priceEl.innerText = '$' + total.toFixed(2);
        }
        console.log("Qty:", qty);
        console.log("Original Price:", originalPrice);
        console.log("Custom Extra:", customExtra);
        console.log("Final Total:", total);
    }

    // Initial run
    updatePriceWithout();

    document.addEventListener("input", function () {
        setTimeout(updatePriceWithout, 300);
    });

    document.addEventListener("change", function () {
        setTimeout(updatePriceWithout, 300);
    });

    document.addEventListener("click", function () {
        setTimeout(updatePriceWithout, 300);
    });

});

}
//snj1
(function () {

  const LOCKED_VARIANT_IDS = [
    '47348904263898', // $60
    '47348904296666', // $120
    //'47349138718938'
  ];

  function lockAddonQuantities() {
    document
      .querySelectorAll('input[data-quantity-variant-id]')
      .forEach(input => {

        const variantId = input.getAttribute('data-quantity-variant-id');
        if (!LOCKED_VARIANT_IDS.includes(variantId)) return;

        // Lock input
        input.readOnly = true;
        input.disabled = true;
        input.style.pointerEvents = 'none';
        input.style.backgroundColor = '#f3f3f3';

        // Lock + / - buttons
        const wrapper = input.closest('quantity-input');
        if (!wrapper) return;

        wrapper.querySelectorAll('button').forEach(btn => {
          btn.disabled = true;
          btn.style.pointerEvents = 'none';
          btn.style.opacity = '0.35';
        });

        console.log('🔒 Quantity locked for variant:', variantId);
      });
  }

  // Run immediately
  document.addEventListener('DOMContentLoaded', lockAddonQuantities);

  // 🔥 OBSERVE CART DOM CHANGES (THIS IS THE KEY)
  const observer = new MutationObserver(() => {
    lockAddonQuantities();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

})();
//snj2
(function () {

  // 🔐 Variant IDs for which REMOVE should be hidden
  const LOCKED_VARIANT_IDS = [
    '47348904263898', // $60
    '47348904296666', // $120
    //'47349138718938'
  ];

  function hideRemoveButtons() {

    document
      .querySelectorAll('cart-remove-button a[href*="/cart/change"]')
      .forEach(link => {

        LOCKED_VARIANT_IDS.forEach(variantId => {
          if (link.href.includes(`id=${variantId}`)) {

            //console.log('🚫 Hiding remove button for variant:', variantId);

            const removeBtn = link.closest('cart-remove-button');
            if (removeBtn) {
              removeBtn.style.display = 'none';
              removeBtn.style.pointerEvents = 'none';
            }
          }
        });

      });
  }

  // Run on page load
  document.addEventListener('DOMContentLoaded', hideRemoveButtons);

  // 🔥 Re-run after any cart DOM update
  const observer = new MutationObserver(hideRemoveButtons);
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

})();
//removing URL for flat products:
(function () {

  // 🔐 Flat fee variant IDs
  const LOCKED_VARIANT_IDS = [
    '47348904263898', // $60
    '47348904296666'  // $120
  ];

  function disableFlatProductLinks() {

    document.querySelectorAll('.cart-item__name').forEach(link => {

      // Check if link URL contains one of our variant IDs
      LOCKED_VARIANT_IDS.forEach(variantId => {
        if (link.href.includes(`variant=${variantId}`)) {

          //console.log('🔒 Disabling link for variant:', variantId);

          link.setAttribute('href', '#');
          link.style.pointerEvents = 'none';
          link.style.cursor = 'default';
          link.style.textDecoration = 'none';
        }
      });

    });
  }

  // Run on page load
  document.addEventListener('DOMContentLoaded', disableFlatProductLinks);

  // Re-run when cart updates dynamically (Dawn AJAX)
  const observer = new MutationObserver(disableFlatProductLinks);
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

})();
//snj3
document.addEventListener('DOMContentLoaded', function() {
    const clearCartButton = document.getElementById('clear-cart');

    if (clearCartButton) {
        clearCartButton.addEventListener('click', function() {
            if (!confirm("Are you sure you want to clear your cart?")) return;

            fetch('/cart/clear.js', { 
                method: 'POST', 
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => response.json())
            .then(data => {
                console.log('Cart cleared', data);
                window.location.reload(); // reload to show empty cart
            })
            .catch(error => console.error('Error clearing cart:', error));
        });
    }
});
//snj4

(function () {

  function reloadOnRemove(e) {
    const link = e.target.closest('a[href*="/cart/change"]');
    if (!link) return;

    const url = new URL(link.href);
    const qty = url.searchParams.get('quantity');

    // If quantity is being set to 0 → item removal
    if (qty === '0') {
      console.log('🗑 Item removed — reloading cart page...');
      setTimeout(() => {
        location.reload();
      }, 700); // small delay so Shopify finishes AJAX update
    }
  }

  document.addEventListener('click', reloadOnRemove);

})();

//snj 5

document.addEventListener('change', function (e) { 
  
  const select = e.target;
  //console.log('onchange1: '+select.name);
  if (!select || select.tagName !== 'SELECT') return;
  if (!select.name.includes('How many colors and sides')) return;
  //if (!select.name.includes('Screen Printing Fee')) return;

  const VARIANT_MAP = [
    { match: '1 color, 1 side', variantId: '47348904263898' }, // $60
    { match: '2 color, 1 side', variantId: '47348904296666' }  // $120
  ];

  let selectedVariantId = null;

  VARIANT_MAP.forEach(item => {
    if (select.value.toLowerCase().includes(item.match.toLowerCase())) {
      selectedVariantId = item.variantId;
    }
  });

  // --- get product handle safely ---
  let form = select.closest('form');
  let handle = form?.dataset?.productHandle || window.location.pathname.split('/').pop();

  // --- remove if no variant ---
  if (!selectedVariantId) {
    if (handle) {
      let store = JSON.parse(sessionStorage.getItem('flat_variant_map') || '{}');
      delete store[handle];
      sessionStorage.setItem('flat_variant_map', JSON.stringify(store));
    }
    return;
  }

  // --- store per-product in sessionStorage ---
  if (handle) {
    let store = JSON.parse(sessionStorage.getItem('flat_variant_map') || '{}');
    store[handle] = selectedVariantId;
    sessionStorage.setItem('flat_variant_map', JSON.stringify(store));
    console.log(`💾 11Stored flat variant for ${handle}:`, selectedVariantId);
  }
});

//snj6
//end snj 

//snj 7
/*
(function ($) {

  //console.log('🚀 FINAL PRICE ENGINE (TP + PPLR) LOADED');

  // 🔹 Get Tier Price based on qty
  function getTierPrice(qty) {

    if (!window.tpObserver || !tpObserver.tierPrices) return 0;

    let tierPrice = 0;

    tpObserver.tierPrices.forEach(t => {
      if (qty >= parseInt(t.qty)) {
        tierPrice = parseFloat(t.price);
      }
    });

    return tierPrice;
  }

  // 🔹 Get PPLR custom options price
  function getPPLRTotal() {

    let total = 0;

    $('.pplr_select').each(function () {
      let selected = $(this).find('option:selected');
      let price = parseFloat(selected.attr('data-pplr_price')) || 0;
      total += price;
    });

    $('input[type="radio"]:checked').each(function () {
      total += parseFloat($(this).attr('data-pplr_price')) || 0;
    });

    $('input[type="checkbox"]:checked').each(function () {
      total += parseFloat($(this).attr('data-pplr_price')) || 0;
    });

    return total;
  }

  // 🔥 MAIN CALCULATION
  function calculateFinal() {

    let qty = parseInt($('[name="quantity"]').val(), 10) || 1;

    // ✅ Tier price
    let tierPrice = getTierPrice(qty);

    // ✅ Variant diff (IMPORTANT)
    let basePrice = parseFloat(tpObserver.baseProductPrice) || 0;
    let currentPrice = parseFloat(tpObserver.productPrice) || basePrice;

    let variantDiff = currentPrice - basePrice;
    if (variantDiff < 0) variantDiff = 0;

    // ✅ PPLR
    let pplrTotal = getPPLRTotal();

    // 🔥 FINAL PRICE PER ITEM
    let finalPerItem = tierPrice + variantDiff + pplrTotal;

    let subtotal = (finalPerItem * qty).toFixed(2);

    //console.log('----------------------');
    //console.log('Qty:', qty);
    //console.log('Tier Price:', tierPrice);
    //console.log('Variant Diff:', variantDiff);
    //console.log('PPLR Total:', pplrTotal);
    //console.log('Final Per Item:', finalPerItem);
    //console.log('Subtotal:', subtotal);

    // 🔥 APPLY TP (important for UI sync)
    applyTierClick(qty);

    // 🔥 UPDATE SUBTOTAL UI (force override)
    updateSubtotalUI(subtotal);
  }

  // 🔹 Apply correct tier row click
  function applyTierClick(qty) {

    let selectedRow = null;

    $('.itier_price').each(function () {
      let minQty = parseInt($(this).attr('qty'));
      if (qty >= minQty) {
        selectedRow = $(this);
      }
    });

    if (selectedRow) {
      selectedRow.trigger('click');
    }
  }

  // 🔥 Force UI update (prevents override issue)
function updateSubtotalUI(total) {

  if (!window.tpObserver) return;

  // ✅ Ensure $ format
  let formatted = '$' + parseFloat(total).toFixed(2);

  let el = jQuery('#tp-subtotal span');

  if (!el.length) {
    console.log('❌ Subtotal element not found');
    return;
  }

  // 🔥 Force update (prevents overwrite)
  setTimeout(() => el.text(formatted), 50);
  setTimeout(() => el.text(formatted), 200);
  setTimeout(() => el.text(formatted), 500);

  console.log('✅ Subtotal Updated UI:', formatted);
}

  // 🔥 EVENTS

  // Qty buttons
  $(document).on('click', '.quantity__button', function () {
    setTimeout(calculateFinal, 120);
  });

  // Qty input
  $(document).on('input', '[name="quantity"]', function () {
    calculateFinal();
  });

  // PPLR options
  $(document).on('change', '.pplr_select, input[type="radio"], input[type="checkbox"]', function () {
    calculateFinal();
  });

  // Variant change
  $(document).on('change', 'variant-selects select', function () {
    setTimeout(calculateFinal, 600);
  });

  // Initial run
  $(document).ready(function () {
    setTimeout(calculateFinal, 1000);
  });

})(jQuery);


//snj 8 new for choose your size custom varient
document.addEventListener('change', function (e) {

  const select = e.target;

  if (!select || select.tagName !== 'SELECT') return;

  // 👉 Target ONLY your field
  if (!select.name.includes('Choose your size')) return;

  let selectedOption = select.options[select.selectedIndex];

  let price = parseFloat(selectedOption.getAttribute('data-pplr_price')) || 0;

  // --- get product handle ---
  let form = select.closest('form');
  let handle = form?.dataset?.productHandle || window.location.pathname.split('/').pop();

  if (!handle) return;

  let store = JSON.parse(sessionStorage.getItem('pplr_size_map') || '{}');

  if (price > 0) {
    store[handle] = price;
    console.log(`💾 Stored SIZE price for ${handle}:`, price);
  } else {
    delete store[handle];
  }

  sessionStorage.setItem('pplr_size_map', JSON.stringify(store));

});*/

//snj 9
document.addEventListener("DOMContentLoaded", function () {

    function updateLowestPrice() { 
      console.log('test');
        var table = document.querySelector('#lb-vd-table');
        if (!table) return;

        var rows = table.querySelectorAll('tbody tr');
        if (!rows.length) return;

        var lastRow = rows[rows.length - 1];
        var priceCell = lastRow.querySelector('.money');
        if (!priceCell) return;

        var priceText = priceCell.textContent.trim();

        var target = document.querySelector('.TP_Custom_base_Price');
        if (target) {
            target.textContent = 'As low as : ' + priceText;
        }
    }

    // Run once after slight delay (Shopify loads content dynamically)
    setTimeout(updateLowestPrice, 3000);

});


//snj 10 
document.addEventListener('DOMContentLoaded', function () {


  function updateTierPrice() {
  const hasTierTable = document.querySelector('.quantity_break_table');

  if (!hasTierTable) {
    console.log("No tier table → script disabled");
    return; //  STOP EVERYTHING
  }
    console.log("Tier table found → script active");

    const table = document.querySelector('.quantity_break_table');
    const qtyInput = document.querySelector('.quantity__input');

    if (!table || !qtyInput) return false;

    let qty = parseInt(qtyInput.value);
    if (!qty || qty <= 0) return true;

    let rows = table.querySelectorAll('tbody tr');
    let matchedPrice = null;

    rows.forEach(row => {

      let min = parseInt(row.getAttribute('data-min'));
      let maxAttr = row.getAttribute('data-max');
      let max = maxAttr.includes('+') ? 999999 : parseInt(maxAttr);

      if (qty >= min && qty <= max) {

        let priceText = row.querySelector('td:last-child').innerText;
        let price = priceText.replace(/[^0-9.]/g, '');

        matchedPrice = parseFloat(price);

        rows.forEach(r => r.classList.remove('qty-row-active'));
        row.classList.add('qty-row-active');
      }
    });

    if (matchedPrice) {

      let total = (matchedPrice * qty).toFixed(2);

      let targetDiv = document.querySelector('.bss-b2b-cart-item-qty-table');

      if (targetDiv) {
      //hiding here default price from product page because we are showing custom price  
      setTimeout(function () {
        var el = document.querySelector('.price-item--regular');
        if (el) el.style.display = 'none';
      }, 100);
      

        let existing = document.querySelector('.custom-tier-price');
        if (existing) existing.remove();

        let priceSpan = document.createElement('div');
        priceSpan.className = 'custom-tier-price';
        priceSpan.innerText = '$' + total + '  USD';

        //  STYLE
        priceSpan.style.fontSize = '18px';
        priceSpan.style.fontWeight = '700';
        priceSpan.style.color = '#bc3635';
        priceSpan.style.marginBottom = '10px';

        targetDiv.parentNode.insertBefore(priceSpan, targetDiv);
      }

      return true;
    }

    return false;
  }

  // 🔁 RETRY (ONLY if table exists)
  function initTierPrice(retry = 0) {

    let success = updateTierPrice();

    if (!success && retry < 10) {
      setTimeout(function () {
        initTierPrice(retry + 1);
      }, 300);
    }
  }

  //  INITIAL RUN
  setTimeout(function () {
    initTierPrice();
  }, 300);

  //  EVENTS (ONLY bind if table exists)

  document.addEventListener('change', function (e) {
    if (e.target.matches('.quantity__input')) {
      setTimeout(updateTierPrice, 100);
    }
  });

  document.addEventListener('click', function (e) {

    if (e.target.closest('.quantity__button')) {
      setTimeout(updateTierPrice, 150);
    }

    if (e.target.closest('.quantity_break_table tr')) {
      setTimeout(updateTierPrice, 300);
    }
 
  });

});

//snj 11
/*
document.addEventListener('change', function(e) {

  if (e.target.name === 'id') { // Shopify variant selector
    console.log('Variant changed:', e.target.value);

    let targetDiv = document.querySelector('.bss-b2b-cart-item-qty-table');

      if (targetDiv) {
      //hiding here default price from product page because we are showing custom price  
      setTimeout(function () {
        var el = document.querySelector('.price-item--regular');
        if (el) el.style.display = 'none';
      }, 100);   
    }

  }

}); 

//snj12
document.addEventListener("DOMContentLoaded", function () 
{

    const qtySelector = 'input[name="quantity"]';
    const priceSelector = '.price'; // 🔥 change if needed
    const tableSelector = '.quantity_break_table tbody tr';

    function getPQty() {
        const qtyInput = document.querySelector(qtySelector);
        return qtyInput ? parseInt(qtyInput.value) || 1 : 1;
    }

	function getTierPriceFrombssb2bTable(qty) {

	  const rows = document.querySelectorAll('.quantity_break_table tbody tr');

	  for (let row of rows) {

		let min = parseInt(row.getAttribute('data-min'));
		let maxAttr = row.getAttribute('data-max');

		// handle 250+
		let max = maxAttr.includes('+') ? 999999 : parseInt(maxAttr);

		if (qty >= min && qty <= max) {

		  let priceText = row.querySelector('td:last-child').innerText.trim();
		  let price = parseFloat(priceText.replace(/[^0-9.]/g, ''));

		  return price;
		}
	  }

	  return null;
	}	

    // 🔥 Get paid custom option price
    function getCustomZepOptionPrice() {

    let extra = 0;

    document.querySelectorAll('select.pplraddprice').forEach(select => {

        // ✅ SKIP hidden / duplicate selects
        if (select.offsetParent === null) return;

        let selectedOption = select.options[select.selectedIndex];
        if (!selectedOption) return;

        let price = parseFloat(selectedOption.dataset.pplr_price || 0);

        if (price > 0) {

            extra += price;

            console.log("bss-b2b Custom Option Selected:");
            console.log("bss-b2b Label:", selectedOption.text.trim());
            console.log("bss-b2b Price from data-pplr_price:", price);
            console.log("-------------------");
        }
    });

    // 🔹 Handle HIDDEN INPUT (Font etc.)
    document.querySelectorAll('input.pplraddprice[type="hidden"]').forEach(input => {

        let price = parseFloat(input.dataset.pplr_price || 0);
        let value = input.value || '';

        // Only if value exists AND price exists
        if (value.trim() !== '' && price > 0) {

            extra += price;

            console.log("bss-b2b Hidden Option:");
            console.log("bss-b2b Label:", input.getAttribute('data_name'));
            console.log("bss-b2b Selected:", value);
            console.log("bss-b2b Price:", price);
            console.log("-------------------");
        }
    });    
    console.log("bss-b2b Total Custom Extra (once):", extra);
    //console.log("================================");

    return extra;
}   

    function updatePriceTPbssB2B() {

      let qty = getPQty();
      let tierPrice = getTierPriceFrombssb2bTable(qty);
      let customExtra = getCustomZepOptionPrice();
      let priceEl = document.querySelector(priceSelector);

      if (!priceEl) return;

      if (tierPrice !== null) {

          // 🔥 Correct Formula
          //let total = (tierPrice * qty) + customExtra;
          let total = (tierPrice + customExtra) * qty;
          let custom_tier_price = '$' + total.toFixed(2)+ ' USD';

			let el = document.querySelector('.custom-tier-price');

			if (el) {
			  el.innerText = custom_tier_price;
			}
      }
    }

    // 🔥 Listen to everything
    document.addEventListener("input", function () { 
    let targetDiv = document.querySelector('.bss-b2b-cart-item-qty-table');

      if (targetDiv) {	
        setTimeout(updatePriceTPbssB2B, 300);
	  }
    });

    document.addEventListener("change", function () {
    let targetDiv = document.querySelector('.bss-b2b-cart-item-qty-table');

      if (targetDiv) {		
        setTimeout(updatePriceTPbssB2B, 300);
	  }
    });

    document.addEventListener("click", function () {
    let targetDiv = document.querySelector('.bss-b2b-cart-item-qty-table');

      if (targetDiv) {		
        setTimeout(updatePriceTPbssB2B, 300);
	  }
    });

})*/


///hide add to cart
(function () {

  const quoteBtn = document.getElementById('custom-form-btn');

  function getZeptoSelections() {
    let text = '';

    document.querySelectorAll('.pplr_select').forEach(select => {
      const opt = select.options[select.selectedIndex];

      if (opt) {
        text += ' ' + opt.text.toLowerCase();
      }
    });

    return text;
  }

  function isPriceZero() {

    console.log('checking price');

    return new Promise((resolve) => {

      console.log('Enter in function');

      setTimeout(() => {

        console.log('Enter in timeout function');

        const priceEl = document.querySelector(
          '.price__regular .price-item--regular, .price-item--sale, .price--large'
        );

        console.log('price element:', priceEl);

        if (!priceEl) {
          resolve(false);
          return;
        }

        const match = priceEl.textContent.match(/\d+(\.\d+)?/);

        if (!match) {
          console.log('No number found in:', priceEl.textContent);
          resolve(false);
          return;
        }

        const price = parseFloat(match[0]);

        console.log('Final parsed price:', price);

        resolve(price === 0);

      }, 300);

    });
  }

  async function updateQuoteUI() {

    const text = getZeptoSelections();

    const needsQuoteByOption =
      text.includes('3+') ||
      text.includes('3 +') ||
      text.includes('2 sides') ||
      text.includes('embroidered logo');

    const priceZero = await isPriceZero();

    const needsQuote = needsQuoteByOption || priceZero;

    const addToCartBtn =
      document.querySelector('form[action*="/cart/add"] button[type="submit"]');

    const buyNowWrap =
      document.querySelector('.shopify-payment-button');

    const productTitle =
      document.querySelector('.product__title h1, .product__title h2')?.innerText || '';

    if (needsQuote) {

      if (quoteBtn) {
        quoteBtn.href = `/pages/quote?product=${encodeURIComponent(productTitle)}`;
        quoteBtn.style.display = 'inline-block';
      }

      if (addToCartBtn) addToCartBtn.style.display = 'none';
      if (buyNowWrap) buyNowWrap.style.display = 'none';

    } else {

      if (quoteBtn) {
        quoteBtn.style.display = 'none';
      }

      if (addToCartBtn) addToCartBtn.style.display = '';
      if (buyNowWrap) buyNowWrap.style.display = '';
    }
  }

  updateQuoteUI();

  document.addEventListener('change', function (e) {
    if (e.target.classList.contains('pplr_select')) {
      updateQuoteUI();
    }
  });

})();


//snj13 
/*document.addEventListener('DOMContentLoaded', function() {

    console.log('Custom price override loaded');

    const customPrices = {
        '8717931380954': '$209.93',
        '9203156582618': '$30.50',
        '8717978566874': '$27.42',
        '8717944291546': '$279.99',
        '8717941670106': '$64.94',
        '8717963526362': '$267.92',
        '8717986595034': '$56.33'

    };

    document.querySelectorAll('.product-card-wrapper').forEach(function(card) {

        const productLink = card.querySelector('a[href*="/products/"]');

        if (!productLink) return;

        const href = productLink.getAttribute('href');

        let productId = '';

        // Match product ID from card links
        if (href.includes('rolling-supply-bag-with-keyless-security-c2-89a-c2-a2-and-heavy-duty-wheels')) {
            productId = '8717931380954';
        }
        else if (href.includes('the-rifkin-safety')) {
            productId = '9203156582618';
        }
        else if (href.includes('stock-keyless-security')) {
            productId = '8717978566874';
        }
        else if (href.includes('classroom-cell-phone-kit-with-arcolock-7')) {
            productId = '8717944291546';
        }
        //
        else if (href.includes('briefcase-style-locking-courier-bag')) {
            productId = '8717941670106';
        }
        else if (href.includes('fire-shield-courier-sacs-locking')) {
            productId = '8717963526362';
        }
        else if (href.includes('specimen-bags')) {
            productId = '8717986595034';
        }                        

        if (!customPrices[productId]) return;

        console.log('Updating Product:', productId);

        const priceArea = card.querySelector('.card-information .price');

        if (priceArea) {
            priceArea.innerHTML = customPrices[productId];
            console.log('Price Updated To:', customPrices[productId]);
        }

    });

});*/

//snj font initially name remove 
document.addEventListener('change', function(e) {

    const select = e.target;

    if (
        select.name === 'properties[What type of personalization would you like?]' &&
        select.value === 'Embroidered Text (Free)'
    ) {

        document.querySelectorAll('.swtooltip span').forEach(function(el){

            let txt = el.textContent;

            txt = txt.replace('DenverScript ','');
            txt = txt.replace('Helsinki ','');
            txt = txt.replace('Calgary ','');
            txt = txt.replace('Tallahassee ','');

            el.textContent = txt;

        });

    }

}); 

//snj add to cart button disabled without selection
// function validateOptions() {

//     const addToCartBtn = document.querySelector('[name="add"]');
//     if (!addToCartBtn) return;

//     const selects = document.querySelectorAll('select[name^="options["]');

//     let valid = true;

//     selects.forEach(select => {
//         if (
//             select.selectedIndex === 0 ||
//             select.value.trim() === ""
//         ) {
//             valid = false;
//         }
//     });

//     addToCartBtn.disabled = !valid;
// }
// SNJ - Add to Cart validation

function validateOptions() {

    const addToCartBtn = document.querySelector('[name="add"]');

    if (!addToCartBtn) {
        console.log('❌ Add to Cart button not found.');
        return;
    }

    let valid = true;
    let emptyCount = 0;


    // =================================
    // SHOPIFY PRODUCT OPTIONS
    // =================================

    const selects = document.querySelectorAll(
        'select[name^="options["]'
    );

    const checkedOptions = {};

    console.log('================================');
    console.log('🔍 CHECKING PRODUCT OPTIONS');
    console.log('Found select elements:', selects.length);

    selects.forEach((select) => {

        const name = select.name;
        const value = select.value.trim();

        // Ignore duplicate Shopify options
        if (checkedOptions[name]) {
            console.log(
                '↪️ Skipping duplicate:',
                name
            );
            return;
        }

        checkedOptions[name] = true;

        const isEmpty = value === '';

        console.log(
            'Product Option:',
            name,
            '| Value:',
            value || '(empty)',
            '|',
            isEmpty ? '❌ EMPTY' : '✅ FILLED'
        );

        if (isEmpty) {
            valid = false;
            emptyCount++;
        }
    });


    // =================================
    // PRODUCT PERSONALIZER OPTIONS
    // =================================

    console.log('--------------------------------');
    console.log('🔍 CHECKING PERSONALIZER DROPDOWNS');

    const personalizerSelects = document.querySelectorAll(
        'select.cstmfy_c_required'
    );

    console.log(
        'Required Personalizer dropdowns:',
        personalizerSelects.length
    );

    personalizerSelects.forEach((select) => {

        const value = select.value.trim();

        const isEmpty = value === '';

        console.log(
            'Personalizer:',
            select.name || '(no name)',
            '| Value:',
            value || '(empty)',
            '|',
            isEmpty ? '❌ EMPTY' : '✅ FILLED'
        );

        if (isEmpty) {
            valid = false;
            emptyCount++;
        }
    });


    // =================================
    // FINAL RESULT
    // =================================

    addToCartBtn.disabled = !valid;

    console.log('--------------------------------');
    console.log(
        'Total empty required fields:',
        emptyCount
    );

    console.log(
        'Add to Cart:',
        valid ? '✅ ENABLED' : '❌ DISABLED'
    );

    console.log('================================');
}


// =================================
// INITIAL CHECK
// =================================

document.addEventListener('DOMContentLoaded', function() {
    validateOptions();
});


// =================================
// CHECK WHEN ANY DROPDOWN CHANGES
// =================================

document.addEventListener('change', function(e) {

    if (e.target.tagName === 'SELECT') {

        console.log(
            '🔄 DROPDOWN CHANGED:',
            e.target.name || '(no name)',
            '| Value:',
            e.target.value
        );

        // Give Shopify/Product Personalizer
        // time to finish updating
        setTimeout(function() {
            validateOptions();
        }, 300);
    }

});

// Initial page load  
window.addEventListener("load", function () {
    setTimeout(validateOptions, 300);
});

// Run after Shopify & other apps finish
document.addEventListener("change", function (e) {

    if (!e.target.matches('select[name^="options["]')) return;

    clearTimeout(window.optionValidationTimer);

    window.optionValidationTimer = setTimeout(function () {
        validateOptions();
    }, 300); // Try 500ms first. Increase to 800-1000ms if needed.

});
